import React, { useState, useEffect, useRef } from 'react';

// ─── Wallet ───────────────────────────────────────────────
// Cash sitting outside the portfolio: CSFloat store credit, crypto, and money
// in the bank. Collapsed it is one number, the total you could spend right now.
// Hovering opens the breakdown, where available and pending are separated —
// pending money is real but you cannot buy with it yet, so the two must never
// be added into a single figure the eye can mistake for spendable cash.
//
// CSFloat is read by the extension whenever you open csfloat.com, so that row
// is not editable here. Nothing can read a bank or an exchange for you, so
// those two are typed in and saved straight back to your account.
//
// The CSFloat figure is also rolled forward from your trades between readings,
// because a reading is only current while you have csfloat.com open. Buy
// something at noon and the row would otherwise still show this morning's
// balance. The server does that arithmetic and explains itself in
// server/services/csfloatLedger.js; what matters here is that the number shown
// may be a projection rather than a reading, and that the difference has to be
// visible. A projected figure is marked, and the movements behind it are
// listed, so the balance is never a number you have to take on faith.

const SOURCES = [
  { id: 'csfloat', label: 'CSFloat', managed: true },
  { id: 'crypto',  label: 'Crypto' },
  { id: 'bank',    label: 'Bank' },
];

const money = (v) => `$${(Number(v) || 0).toLocaleString('en-US', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;

/** Signed, for a movement: the sign is the whole point of the figure. */
const signed = (v) => `${v > 0 ? '+' : v < 0 ? '-' : ''}${money(Math.abs(v))}`;

function relativeTime(iso) {
  const then = Date.parse(iso || '');
  if (!Number.isFinite(then)) return null;

  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1)    return 'just now';
  if (mins < 60)   return `${mins}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return `${Math.round(mins / 1440)}d ago`;
}

/** Same scale the other way round, for money that unlocks in the future. */
function countdown(iso) {
  const then = Date.parse(iso || '');
  if (!Number.isFinite(then)) return null;

  const mins = Math.round((then - Date.now()) / 60000);
  if (mins <= 0)   return 'any moment';
  if (mins < 60)   return `in ${mins}m`;
  if (mins < 1440) return `in ${Math.round(mins / 60)}h`;
  const days = Math.round(mins / 1440);
  return `in ${days} day${days !== 1 ? 's' : ''}`;
}

export default function WalletChip({
  wallets, onSave, onRefreshCrypto, onLoadLedger,
  transfers = [], cryptoIsAutomatic, onResolveTransfer, onDeposit,
  useBank = true, zone,
}) {
  const [open, setOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showLedger, setShowLedger] = useState(false);
  const [ledger, setLedger] = useState(null);
  // The id currently being answered, so its buttons can go quiet rather than
  // letting a second click ask the same question twice.
  const [answering, setAnswering] = useState(null);
  const wrapRef = useRef(null);

  // Hovering opens it, but editing a field and having the panel vanish because
  // the pointer strayed a few pixels is maddening, so a click pins it open.
  const [pinned, setPinned] = useState(false);

  useEffect(() => {
    if (!pinned) return;
    const away = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) { setPinned(false); setOpen(false); } };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [pinned]);

  const rows = SOURCES.filter(source => useBank || source.id !== 'bank').map(source => ({
    ...source,
    balance: Number(wallets?.[source.id]?.balance) || 0,
    pending: Number(wallets?.[source.id]?.pending) || 0,
    updatedAt: wallets?.[source.id]?.updatedAt || null,
    // Crypto is typed in by hand until wallet addresses are configured, at
    // which point the server reads the chains and typing over it would only be
    // overwritten by the next refresh.
    managed: source.managed || wallets?.[source.id]?.auto === true,
    errors: wallets?.[source.id]?.errors || [],
    // Present only on CSFloat, and only once a reading exists to project from.
    projection: wallets?.[source.id]?.projection || null,
    // Money a purchase could not take because the balance was short. Real debt
    // against this wallet, settled out of the next money added.
    shortfall: Number(wallets?.[source.id]?.shortfall) || 0,
  }));

  const crypto = rows.find(r => r.id === 'crypto');
  const csfloat = rows.find(r => r.id === 'csfloat');

  const drift = wallets?.csfloat?.drift || null;
  // Movements applied since the last reading. Zero is the normal, quiet case:
  // it means the reading is still current and nothing needs explaining.
  const appliedCount = csfloat?.projection?.applied || 0;
  // Sales whose money exists but has not moved yet, waiting out trade protection.
  const upcomingCount = csfloat?.projection?.upcoming || 0;

  // Fetched when the list is actually opened rather than on every page load.
  // Nothing above needs it: the counts and the projected figures already come
  // down with the wallet, so the itemization is only worth a request when
  // somebody asks to see it.
  const toggleLedger = async (e) => {
    e.stopPropagation();
    const next = !showLedger;
    setShowLedger(next);
    if (next && !ledger) setLedger(await onLoadLedger?.() || null);
  };

  const answer = async (e, transfer, source) => {
    e.stopPropagation();
    setAnswering(transfer.id);
    try { await onResolveTransfer?.(transfer.id, source); } finally { setAnswering(null); }
  };

  const refresh = async (e) => {
    e.stopPropagation();
    setRefreshing(true);
    try { await onRefreshCrypto?.({ force: true }); } finally { setRefreshing(false); }
  };

  const totalAvailable = rows.reduce((sum, r) => sum + r.balance, 0);
  const totalPending   = rows.reduce((sum, r) => sum + r.pending, 0);

  return (
    <div
      ref={wrapRef}
      className={`wallet-chip-wrap ${zone ? 'zn' : ''}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => { if (!pinned) setOpen(false); }}
    >
      <button
        type="button"
        className={`wallet-chip ${open ? 'open' : ''}`}
        onClick={() => { setPinned(p => !p); setOpen(true); }}
        title="Your spendable cash. Hover for the breakdown, click to keep it open."
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 7V5a2 2 0 0 0-2-2H5a2 2 0 0 0 0 4h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5"/>
          <path d="M16 12h.01"/>
        </svg>
        <span className="wallet-chip-total">{money(totalAvailable)}</span>
        {totalPending > 0 && (
          <span className="wallet-chip-pending">+{money(totalPending)}</span>
        )}
      </button>

      {open && (
        <div className="wallet-panel">
          <div className="wallet-panel-head">
            <span>Source</span>
            <span>Available</span>
            <span>Pending</span>
          </div>

          {rows.map(row => (
            <WalletRow key={row.id} row={row} onSave={onSave} onDeposit={onDeposit} />
          ))}

          <div className="wallet-panel-total">
            <span>Total</span>
            <span className="num">{money(totalAvailable)}</span>
            <span className={`num pending ${totalPending > 0 ? '' : 'none'}`}>
              {totalPending > 0 ? money(totalPending) : 'n/a'}
            </span>
          </div>

          {crypto?.errors?.length > 0 && (
            <p className="wallet-panel-warn">
              {crypto.errors.length} source{crypto.errors.length !== 1 ? 's' : ''} could not be read,
              so the crypto figure may be short. Settings → Crypto Wallets says which.
            </p>
          )}

          {/* An unexplained change big enough to be a transfer becomes a question.
              Nothing can read your bank, so this is the only moment the site will
              ever have evidence that money moved in or out of it, and one click
              now is worth more than any amount of inference later. */}
          {transfers.map(transfer => (
            <TransferPrompt
              key={transfer.id}
              transfer={transfer}
              cryptoIsAutomatic={cryptoIsAutomatic}
              useBank={useBank}
              busy={answering === transfer.id}
              onAnswer={answer}
            />
          ))}

          {/* The same disagreement, stated passively, once it is too small to be
              a transfer worth asking about. The reading always wins, so the
              figure above is already right; this only explains why it moved by
              something the trades do not account for. */}
          {drift && transfers.length === 0 && (
            <p className="wallet-panel-drift">
              Last sync found {money(Math.abs(drift.balance))} {drift.balance > 0 ? 'more' : 'less'} than
              your trades account for. The figure above is CSFloat's own, so it is still right.
            </p>
          )}

          {csfloat?.projection?.clamped && (
            <p className="wallet-panel-warn">
              Your trades subtract more than the last reading held, so the CSFloat row is
              showing zero rather than a negative. Open csfloat.com to re-read it.
            </p>
          )}

          {/* Driven by the counts that come down with the wallet, not by the
              ledger itself. The ledger is only fetched once this is clicked, so
              gating the button on it would mean a sale sitting in trade
              protection had no way of ever being seen. */}
          {(appliedCount > 0 || upcomingCount > 0) && (
            <button type="button" className="wallet-ledger-toggle" onClick={toggleLedger}>
              {appliedCount > 0
                ? `${appliedCount} trade${appliedCount !== 1 ? 's' : ''} since the last sync`
                : `${upcomingCount} sale${upcomingCount !== 1 ? 's' : ''} still locked`}
              <span className="wallet-ledger-caret">{showLedger ? '▴' : '▾'}</span>
            </button>
          )}

          {showLedger && <Ledger ledger={ledger} />}

          <div className="wallet-panel-note">
            <div>
              {rows[0].updatedAt
                ? appliedCount > 0
                  ? `CSFloat read ${relativeTime(rows[0].updatedAt)}, then rolled forward through ${appliedCount} trade${appliedCount !== 1 ? 's' : ''}.`
                  : `CSFloat read ${relativeTime(rows[0].updatedAt)} by the extension.`
                : 'Open csfloat.com with the extension installed to fill in the CSFloat row.'}
            </div>
            <div style={{ marginTop: 2 }}>
              {crypto?.managed
                ? `Crypto read from your wallet addresses ${relativeTime(crypto.updatedAt) || 'recently'}.`
                : 'Add wallet addresses in Settings → Crypto Wallets to read crypto automatically.'}
            </div>
            {crypto?.managed && (
              <button type="button" className="wallet-refresh" onClick={refresh} disabled={refreshing}>
                {refreshing ? 'Reading chains…' : 'Refresh crypto'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Transfer question ────────────────────────────────────
// CSFloat's balance moved by something your trades do not account for, which
// means money came in or went out. Only you know from where, so this asks
// rather than guesses.
//
// The suggestion, when there is one, comes from the crypto reading having moved
// by about the same amount at about the same time. It is offered as a default
// and never applied on its own: matching size and timing is good evidence, not
// proof, and silently moving money on the strength of a coincidence would be
// worse than asking a question the user can answer in one click.

function TransferPrompt({ transfer, cryptoIsAutomatic, useBank, busy, onAnswer }) {
  const amount = money(Math.abs(transfer.amount));
  const incoming = transfer.direction === 'in';
  const suggested = transfer.suggestion?.source || null;

  const options = [
    useBank && { id: 'bank', label: 'Cash' },
    { id: 'crypto', label: 'Crypto' },
    { id: 'none',   label: 'Neither' },
  ].filter(Boolean);

  return (
    <div className="wallet-transfer">
      <div className="wallet-transfer-head">
        CSFloat {incoming ? 'gained' : 'lost'} <strong>{amount}</strong> your trades do not explain.
        {' '}{incoming ? 'Where did it come from?' : 'Where did it go?'}
      </div>

      {transfer.suggestion && (
        <div className="wallet-transfer-hint">Looks like {transfer.suggestion.reason}.</div>
      )}

      <div className="wallet-transfer-actions">
        {options.map(option => (
          <button
            key={option.id}
            type="button"
            className={`wallet-transfer-btn ${suggested === option.id ? 'suggested' : ''}`}
            disabled={busy}
            onClick={e => onAnswer(e, transfer, option.id)}
          >
            {option.label}
          </button>
        ))}
      </div>

      {/* Answering "crypto" when crypto reads itself changes no figure, because
          the chain balance was taken after the money left and already counts it.
          Said up front, so a click that moves nothing does not look broken. */}
      {cryptoIsAutomatic && (
        <div className="wallet-transfer-note">
          Crypto is read from your addresses, so choosing it records where the money went
          without changing the figure.
        </div>
      )}
    </div>
  );
}

// ─── CSFloat ledger ───────────────────────────────────────
// Why the CSFloat figure is what it is, one line per movement.
//
// Two lists, and they are genuinely different questions. `applied` is money that
// has already moved but that CSFloat has not been asked about since, so it is
// the difference between the reading and the number on screen. `upcoming` is
// money that exists but has not moved yet: a sale sitting out its trade
// protection, which will shift from pending to available on a date already
// known. Adding those together would be the same mistake the panel avoids at the
// top, so they stay apart and only `applied` is in the balance.

const KIND_LABEL = {
  purchase: 'Bought',
  sale:     'Sold',
  release:  'Unlocked',
};

function Ledger({ ledger }) {
  if (!ledger) return <p className="wallet-ledger-empty">Loading…</p>;

  if (!ledger.anchored) {
    return (
      <p className="wallet-ledger-empty">
        No CSFloat reading yet, so there is nothing to roll forward from.
        Open csfloat.com with the extension installed.
      </p>
    );
  }

  const applied  = ledger.applied  || [];
  const upcoming = ledger.upcoming || [];

  if (applied.length === 0 && upcoming.length === 0) {
    return <p className="wallet-ledger-empty">Nothing has moved since the last reading.</p>;
  }

  return (
    <div className="wallet-ledger">
      {applied.length > 0 && (
        <>
          <div className="wallet-ledger-head">Since the last sync</div>
          {applied.map(move => <LedgerRow key={`${move.tradeId}-${move.kind}`} move={move} />)}
          <div className="wallet-ledger-sum">
            <span>Net</span>
            <span className={`num ${ledger.delta.available >= 0 ? 'up' : 'down'}`}>
              {signed(ledger.delta.available)}
            </span>
          </div>
        </>
      )}

      {upcoming.length > 0 && (
        <>
          <div className="wallet-ledger-head">Locked in trade protection</div>
          {upcoming.map(move => <LedgerRow key={`${move.tradeId}-${move.kind}`} move={move} upcoming />)}
        </>
      )}
    </div>
  );
}

function LedgerRow({ move, upcoming }) {
  // A release is the only movement with two signs, and the one worth stating as
  // a column change rather than an amount: nothing was earned, the money simply
  // became spendable.
  const amount = move.kind === 'release' ? move.available : (move.available || move.pending);
  const when = upcoming ? countdown(move.at) : relativeTime(move.at);

  return (
    <div className="wallet-ledger-row">
      <span className="wallet-ledger-skin" title={move.skin}>
        <span className={`wallet-ledger-kind ${move.kind}`}>{KIND_LABEL[move.kind] || move.kind}</span>
        {move.skin}
      </span>
      <span className="wallet-ledger-when">{when || 'n/a'}</span>
      <span className={`num ${amount >= 0 ? 'up' : 'down'} ${move.kind === 'sale' ? 'pending' : ''}`}>
        {signed(amount)}
      </span>
    </div>
  );
}

// One row of the breakdown. Managed sources render as plain figures; the rest
// are inputs that commit on blur or Enter, so a half-typed number is never saved.
function WalletRow({ row, onSave, onDeposit }) {
  const [draft, setDraft] = useState(null); // { balance, pending } while editing

  const value = field => (draft ? draft[field] : String(row[field] || ''));

  const edit = (field, raw) => {
    setDraft(d => ({
      balance: String(row.balance || ''),
      pending: String(row.pending || ''),
      ...(d || {}),
      [field]: raw,
    }));
  };

  const commit = () => {
    if (!draft) return;
    const balance = parseFloat(draft.balance) || 0;
    const pending = parseFloat(draft.pending) || 0;
    setDraft(null);
    // Nothing changed: skip the write rather than bumping the saved timestamp.
    if (balance === row.balance && pending === row.pending) return;
    onSave(row.id, { balance, pending });
  };

  // Topping up rather than retyping the total.
  //
  // Both are useful and they are not the same action. Retyping says "the balance
  // is now this", which is what you do after checking your bank. Adding says "I
  // put another 500 in", which is what you do when you actually did, and it
  // neither requires you to know the current figure nor risks fat-fingering it
  // away. The input above still does the first; this does the second.
  const [adding, setAdding] = useState(false);
  const [topUp, setTopUp] = useState('');

  const commitTopUp = () => {
    const amount = parseFloat(topUp) || 0;
    setTopUp('');
    setAdding(false);
    if (amount > 0) onDeposit?.(row.id, amount);
  };

  if (row.managed) {
    // A figure that has been rolled forward past its reading is still a good
    // figure, but it is not the same KIND of thing as one CSFloat just told us,
    // and a wallet that blurs the two is a wallet you cannot reconcile. The dot
    // is the whole distinction: read, or read and then computed.
    const projected = (row.projection?.applied || 0) > 0;
    return (
      <div className="wallet-row managed">
        <span className="wallet-row-label">
          {row.label}
          {projected && (
            <span className="wallet-projected-dot" title="Rolled forward from your trades since the last reading" />
          )}
        </span>
        <span className="num">{money(row.balance)}</span>
        <span className={`num pending ${row.pending > 0 ? '' : 'none'}`}>
          {row.pending > 0 ? money(row.pending) : 'n/a'}
        </span>
      </div>
    );
  }

  return (
    <>
      <div className="wallet-row">
        <span className="wallet-row-label">
          {row.label}
          <button
            type="button"
            className="wallet-add-btn"
            title={`Add to ${row.label} without retyping the total`}
            onClick={e => { e.stopPropagation(); setAdding(a => !a); }}
          >
            +
          </button>
        </span>
        {['balance', 'pending'].map(field => (
          <input
            key={field}
            className={`wallet-input ${field === 'pending' ? 'pending' : ''}`}
            type="number"
            step="0.01"
            min="0"
            inputMode="decimal"
            placeholder="0.00"
            value={value(field)}
            onChange={e => edit(field, e.target.value)}
            onBlur={commit}
            onKeyDown={e => {
              if (e.key === 'Enter') e.currentTarget.blur();
              if (e.key === 'Escape') { setDraft(null); e.currentTarget.blur(); }
            }}
          />
        ))}
      </div>

      {row.shortfall > 0 && (
        <div className="wallet-short">
          {money(row.shortfall)} could not be taken, the balance was short at the time.
          It comes off the next money you add, or type the real balance to clear it.
        </div>
      )}

      {adding && (
        <div className="wallet-topup">
          <span>Add to {row.label}</span>
          <input
            className="wallet-input"
            type="number"
            step="0.01"
            min="0"
            inputMode="decimal"
            placeholder="0.00"
            autoFocus
            value={topUp}
            onChange={e => setTopUp(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') commitTopUp();
              if (e.key === 'Escape') { setTopUp(''); setAdding(false); }
            }}
          />
          <button type="button" className="wallet-topup-go" onClick={commitTopUp}>Add</button>
        </div>
      )}
    </>
  );
}
